import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { DatePicker, TimePicker, DateTimePicker } from '@material-ui/pickers';
import classNames from "classnames";
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Fab from '@material-ui/core/Fab';
import InputAdornment from '@material-ui/core/InputAdornment';
import Slider from '@material-ui/core/Slider';

import AddIcon from '@material-ui/icons/Add';
import CopyIcon from '@material-ui/icons/FileCopy';
import DeleteIcon from '@material-ui/icons/Delete';
import UpIcon from '@material-ui/icons/ExpandLess';
import DownIcon from '@material-ui/icons/ExpandMore';
import PublishIcon from '@material-ui/icons/Publish';
import EmailIcon from '@material-ui/icons/Email';
import PaymentIcon from '@material-ui/icons/Payment';
import ImageIcon from '@material-ui/icons/Image';

import SearchUsersAutocomplete from 'app/components/user/UserSearchAutocomplete';
import RedButton from 'app/components/buttons/RedButton';
import SelectFileDialog from 'app/components/drive/SelectFileDialog';

import useApi from 'app/context/useApi';

const RichImageSlider = withStyles(theme => ({
    root: {
        color: theme.palette.grey[600],
    },
    thumb: {
        backgroundColor: '#fff',
        border: '2px solid currentColor',
        '&:focus, &:hover, &$active': {
            boxShadow: 'inherit',
        },
    },
}))(Slider);

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    fieldContainer: {
        overflowY: 'scroll',
        height: '100%',
        backgroundColor: theme.palette.grey[100],
        maxHeight: '70vh',
        boxShadow: '0px 0px 10px -5px #000 inset'
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    height: {
        height: '100%'
    },
    fieldDeleteButton: {
        color: 'white',
        backgroundColor: theme.palette.error.main,
        '&:hover': {
            backgroundColor: theme.palette.error.dark
        }
    },
    field: {
        width: '100%',
        padding: theme.spacing(3)
    },
    selectedField: {
        backgroundColor: 'white',
        border: '2px solid #ddd',
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(3),
        paddingBottom: theme.spacing(12)
    },
    extendedIcon: {
        marginRight: theme.spacing(1),
      },
    formControl: {
        width: '100%'
    },
    options: {
        padding: theme.spacing(3)
    },
    option: {
        marginBottom: theme.spacing(1)
    },
    buttonGroupBorder: {
        border: `1px solid rgba(0, 0, 0, 0.23)`,
        borderRadius: theme.shape.borderRadius,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: 1,
        paddingBottom: 1,
        marginTop: theme.spacing(3),
    },
}));

const fieldTypes = [
    {
        value: 'header',
        label: 'Header'
    },
    {
        value: 'subheader',
        label: "Subheader"
    },
    {
        value: 'paragraph',
        label: 'Paragraph'
    },
    {
        value: 'image',
        label: 'Image'
    },
    {
        value: 'divider',
        label: 'Divider'
    },
    {
        value: 'textbox',
        label: 'Text Box'
    },
    {
        value: 'email',
        label: 'Email'
    },
    {
        value: 'textarea',
        label: 'Text Area'
    },
    {
        value: 'number',
        label: 'Number Selector'
    },
    {
        value: 'currency',
        label: 'Currency'
    },
    {
        value: 'date',
        label: 'Date Selector'
    },
    {
        value: 'time',
        label: 'Time Selector'
    },
    {
        value: 'datetime',
        label: 'Date & Time Selector'
    },
    {
        value: 'checkbox',
        label: 'Checkbox'
    },
    {
        value: 'options',
        label: 'Options Menu (Drop Down)'
    },
    {
        value: 'upload',
        label: 'File Upload Button'
    },
    {
        value: 'userselect',
        label: 'User Select'
    },
    {
        value: 'emailbutton',
        label: 'Email Button'
    },
]

const notTextFields = [ 'divider', 'paragraph', 'header', 'subheader', 'image' ];

const alignment = [ 'left', 'center', 'right' ];

const noLabel = [ 'header', 'subheader', 'paragraph', 'divider', 'upload', 'emailbutton', 'paymentform', 'image' ];

const noAlign = ['upload', 'divider', 'userselect', 'textbox', 'email', 'textarea', 'number', 'currency', 'datetime', 'date', 'time', 'options', 'checkbox'];

const strictSelectors = [ 'userselect', 'options' ];

const forTitle = [ 'textbox', 'email', 'options', 'userselect' ];

const forSubtitle = [ 'textbox', 'email', 'options', 'userselect', 'datetime', 'number', 'currency', 'date', 'time' ];

const forCustom = [ 'textbox', 'options', 'userselect', 'datetime', 'number', 'date', 'time' ];

const initFieldOptions = {
    group_id: 0,
    type: 'textbox',
    label: '',
    options: [],
    length: 1,
    strict_select: false,
    required: false,
    title: false,
    subtitle: false,
    align: 'left',
    helper_text: '',
    admin_view: false,
    admin_edit: false,
    table: false,
    order_by: false,
    order: 'asc',
    conditional: '',
    email_field: '',
    email_template_id: 0,
    payment_amount_field: '',
    payment_user: 0,
    image_url:'',
    image_width: 100
}

const initFields = [{ options: {...initFieldOptions, type: 'header', label: 'Your Form (click to edit)', align: 'center'}}];

const buildField = (field, classes, selected) => {

    switch (field.options.type) {
        case 'header' :
            return (
                <Typography variant='h3' align={field.options.align}>
                    {field.options.label}
                </Typography>
            )
        case 'subheader' :
            return (
                <Typography variant="h6" align={field.options.align}>
                    {field.options.label}
                </Typography>
            )
        case 'paragraph' :
            return (
                <Typography variant="body1" align={field.options.align}>
                    {field.options.label}
                </Typography>
            )
        case 'image' :
            return (
                <div style={{ textAlign: field.options.align }}>
                    {(field.options.image_url && field.options.image_url !== '') ? (
                        <img 
                            style={{ height: 'auto', maxWidth: '100%', width: field.options.image_width + "%" }} 
                            src={field.options.image_url} 
                            alt={field.options.label}
                        />
                    ) : (
                        <ImageIcon style={{ fontSize: 80 }} />
                    ) }
                </div>
            )
        case 'divider' :
            return (
                <Divider />
            )
        case 'email' :
        case 'textbox' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required && ' *'}
                    </Typography>
                    <TextField
                        id={field.options.label}
                        variant="outlined"
                        fullWidth
                        helperText={field.options.helper_text}
                        required={field.options.required}
                        disabled={!selected}
                        type={field.options.type === 'email' ? 'email' : 'text'}
                    />
                </>
            )
        case 'textarea' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required && ' *'}
                    </Typography>
                    <TextField
                        id={field.options.label}
                        variant="outlined"
                        fullWidth
                        helperText={field.options.helper_text}
                        multiline
                        rows={5}
                        required={field.options.required}
                        disabled={!selected}
                    />
                </>
            )
        case 'number' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required && ' *'}
                    </Typography>
                    <TextField
                        id={field.options.label}
                        type='number'
                        variant="outlined"
                        fullWidth
                        helperText={field.options.helper_text}
                        required={field.options.required}
                        disabled={!selected}
                    />
                </>
            )
        case 'currency' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required && ' *'}
                    </Typography>
                    <TextField
                        id={field.options.label}
                        type="number"
                        step="0.01"
                        variant="outlined"
                        fullWidth
                        helperText={field.options.helper_text}
                        required={field.options.required}
                        disabled={!selected}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            step: "0.01"
                        }}
                    />
                </>
            )
        case 'date' :
                return (
                    <>
                        <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                            {field.options.label}{field.options.required && ' *'}
                        </Typography>
                        <DatePicker
                            inputVariant="outlined"
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            disabled={!selected}
                            format="DD"
                        />
                    </>
                )
        case 'time' :
                return (
                    <>
                        <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                            {field.options.label}{field.options.required && ' *'}
                        </Typography>
                        <TimePicker
                            inputVariant="outlined"
                            variant="outlined"
                            TextFieldComponent={renderDateInput}
                            disabled={!selected}
                        />
                    </>
                )
        case 'datetime' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required && ' *'}
                    </Typography>
                    <DateTimePicker
                        inputVariant="outlined"
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        disabled={!selected}
                    />
                </>
            )
        case 'checkbox' :
            return (
                <FormControlLabel
                    disabled={!selected}
                    label={field.options.label}
                    required={field.options.required}
                    control={
                        <Checkbox required={field.options.required}/>
                    }
                />
            )
        case 'options' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required && ' *'}
                    </Typography>
                    <TextField
                        id={field.options.label}
                        select
                        variant="outlined"
                        fullWidth
                        helperText={field.options.helper_text}
                        required={field.options.required}
                        defaultValue={""}
                        >
                        {field.options.options.map((option, index) => (
                            <MenuItem key={index} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </>
            )
        case 'upload' :
            return (
                <Grid container alignItems='center' justify='center'>
                    <label htmlFor="upload-photo">
                        <input
                            hidden
                            id="upload-photo"
                            name="upload-photo"
                            type="file"
                        />
                        <Tooltip title={field.options.helper_text}>
                            <Fab variant="extended" color='secondary' component="span">
                                <PublishIcon className={field.options.label===`` ? null : classes.extendedIcon} />
                                {field.options.label}
                            </Fab>
                        </Tooltip>
                    </label>
                </Grid>
            )
        case 'userselect' :
            return (
                <>
                    <Typography variant='subtitle2' color="textSecondary" gutterBottom>
                        {field.options.label}{field.options.required ? ' *' : null}
                    </Typography>
                    <SearchUsersAutocomplete
                        closeOnSelect
                        setOnSelect
                        onSelect={() => {}}
                    />
                    <Typography variant="caption" color="textSecondary" style={{marginLeft: 12}}>
                        {field.options.helper_text}
                    </Typography>
                </>
            )
        case 'emailbutton' :
            return (
                <Grid container alignItems='center' justifyContent='center'>
                    <Tooltip title={field.options.helper_text}>
                        <Fab variant="extended" color='secondary' component="span">
                            <EmailIcon className={field.options.label===`` ? null : classes.extendedIcon} />
                            {field.options.label}
                        </Fab>
                    </Tooltip>
                </Grid>
            )
        case 'paymentform' :
            return (
                <Grid container alignItems='center' justifyContent='center'>
                    <Tooltip title={field.options.helper_text}>
                        <Fab variant="extended" color='secondary' component="span">
                            <PaymentIcon className={field.options.label===`` ? null : classes.extendedIcon} />
                            {field.options.label}
                        </Fab>
                    </Tooltip>
                </Grid>
            )
    }
}

const AdvancedField = (props) => {

    const classes = useStyles();

    const {
            field,
            index,
            selected,
            onMoveUp,
            onMoveDown,
            onDelete,
            onSelect,
            onCopy
    } = props;

    const fieldClasses = classNames({
        [classes.field]: true,
        [classes.selectedField]: selected,
    })

    return(

        <div
            className={fieldClasses}
            onClick={(e) => onSelect(index, e)}
            >
            <Grid container direction="column" spacing={3}>
                {selected &&
                    <Grid item>
                        <Tooltip title="Move Up">
                            <IconButton size="small" onClick={e => onMoveUp(index, e)}>
                                <UpIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                }
                <Grid item>
                    {buildField(field, classes, selected)}
                </Grid>
                {selected &&
                    <Grid item>
                        <Grid container direction="row" justify="space-between">
                            <Grid item>
                                <Tooltip title="Move Down">
                                    <IconButton size="small" onClick={e => onMoveDown(index, e)}>
                                        <DownIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        onClick={e => onCopy(e, field, index)}
                                        >
                                        <CopyIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        onClick={e => onDelete(index, e)}
                                        >
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        </div>
    )
};

export default function FormBuilder(props) {

    const classes = useStyles();
    const api = useApi();

    const {
        fields,
        setFields,
        errors,
        newForm,
        type="form",
        election
    } = props;

    const [ activeFieldKey, setActiveFieldKey ] = useState(0);
    const [ groups, setGroups ] = useState([]);
    const [ templates, setTemplates ] = useState([]);

    const [ selectFileDialogOpen, setSelectFileDialogOpen ] = useState(false);
    const [ selectFileType, setSelectFileType ] = useState(null);

    const getGroups = () => api.get('/admin/group').then(res => setGroups(res.groups));
    const getTemplates = () =>  api.get('/admin/email/template').then(res => setTemplates(res.templates));

    useEffect(() => {
        if (newForm) setFields(initFields);
        getGroups();
        getTemplates();
    }, [])

    // Handlers

    const addField = (e) => {

        e.stopPropagation()

        const newFields = [...fields];

        const newField = { options: {}};
        newField.options = {...initFieldOptions}; // Needed to shallow clone. Spread operator doesn't work on deep cloning

        newFields.push(newField);

        setFields(newFields)
        setActiveFieldKey(fields.length)
    }

    const copyField = (e, field, key) => {

        e.stopPropagation()

        const newFields = [...fields];
        const newField = { options: {}};

        newField.options = {...field.options}; // Needed to shallow clone. Spread operator doesn't work on deep cloning

        //newFields.push(newField);
        newFields.splice(key,0,newField);

        setFields(newFields)
        setActiveFieldKey(key + 1)
    }

    const handleFields = (name, event) => {

        if (isError(name)) delete errors[name];

        let value;
        const newFields = [...fields]

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (name === 'type' && value === 'paymentform') {

        }

        newFields[activeFieldKey].options[name] = value;
        setFields(newFields)

    };

    const addOption = () => {

        const newFields = [...fields];

        newFields[activeFieldKey].options.options.push('');

        setFields(newFields)
    }

    const removeOption = (key) => {

        const newFields = [...fields]

        newFields[activeFieldKey].options.options.splice(key, 1);

        setFields(newFields);
    }

    const handleOption = (event, i) => {

        const value = event.target.value;

        const newFields = [...fields];
        const newOptions = [...fields[activeFieldKey].options.options];

        newOptions[i] = value;
        newFields[activeFieldKey].options.options = newOptions;

        setFields(newFields)
    }

    const handleActiveFieldKey = (key, e) => {
        e.stopPropagation()
        setActiveFieldKey(key);
    }

    const handleMoveUp = (key, e) => {
        e.stopPropagation()
        if (key > 0) {
            const previousKey = key - 1;
            const newFields = [...fields];
            const field = newFields.splice(key,1)[0];

            newFields.splice(previousKey,0,field);

            setFields(newFields);
            setActiveFieldKey(previousKey);
        }
    }

    const handleMoveDown = (key, e) => {
        e.stopPropagation()
        if (key < (fields.length - 1)) {
            const nextKey = key + 1;
            const newFields = [...fields];
            const field = newFields.splice(key,1)[0];

            newFields.splice(nextKey,0,field);
            setFields(newFields);
            setActiveFieldKey(nextKey);
        }
    }

    const handleDelete = (key, e) => {
        e.stopPropagation()
        const newFields = [...fields];
        newFields.splice(key,1);
        setFields(newFields);

        const newKey = key - 1;

        if (newKey > -1) setActiveFieldKey(newKey);
        else setActiveFieldKey(null);
    }

    // Error handlers ------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    // Select File Dialog Functions

    const openSelectFileDialog = (type = null) => {
        setSelectFileDialogOpen(true);
        setSelectFileType(type);
    }

    const closeSelectFileDialog = () => {
        setSelectFileDialogOpen(false);
    }

    const closeSelectFileDialogAndAddImage = (file, event) => {
        
        const newFields = [...fields];

        switch (selectFileType) {
            case 'image': 
        
                newFields[activeFieldKey].options.image_url = file.full_url;
                
                break;
        }

        setSelectFileType(null);
        setFields(newFields);
        closeSelectFileDialog();
    }

    const handleImageWidth = (width) => {
        const newFields = [...fields];

        newFields[activeFieldKey].options.image_width = width;

        setFields(newFields);
    }

    const removeImage = () => {
        const newFields = [...fields];

        newFields[activeFieldKey].options.image_url = '';
        newFields[activeFieldKey].options.image_width = 100;

        setFields(newFields);
    }

    // Constants --------------------------------------

    const fieldSelectHelperText = useMemo(() => {
        switch (fields[activeFieldKey]?.type) {
            case 'upload':
                return "To add text to your image upload button type in the field below.";
            case 'datetime':
                return "You will not be able to choose a date, but users will when filling out the form.";
            default: return null;
        }
    }, [fields, activeFieldKey]);

    const allOptions = useMemo(() => {
        let ops = [];

        fields.forEach(f => {
            if (f.options.type === 'options') {
                ops = ops.concat(f.options.options);
            }
        });
        console.log(ops)
        return ops;
    }, [fields]);

    const emailFields = useMemo(() => {
        let ops = [];

        fields.forEach(f => {
            if (f.options.type === 'email') {
                ops = ops.concat(f.options.label);
            }
        });

        return ops;
    }, [fields]);

    const currencyFields = useMemo(() => {
        let ops = [];

        fields.forEach(f => {
            if (f.options.type === 'currency') {
                ops = ops.concat(f.options.label);
            }
        });

        return ops;
    }, [fields]);

    const paymentField = useMemo(() => {
        return fields.find(f => f.options.type === 'paymentform');
    }, [fields]);

    return (
        <Grid container direction="row" className={newForm ? classes.height : null}>
            <Grid
                item
                xs={6}
                className={classes.fieldContainer}
                onClick={(e) => handleActiveFieldKey(null, e)}
                >
                {fields.map((field, index) => (
                    <AdvancedField
                        key={index}
                        field={field}
                        index={index}
                        selected={activeFieldKey === index}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        onDelete={handleDelete}
                        onSelect={handleActiveFieldKey}
                        handleFields={handleFields}
                        onCopy={copyField}
                    />
                ))}
                <div className={classes.buttonContainer}>
                    <Tooltip title="Click to add a new form field.">
                        <Button
                            onClick={addField}
                            variant="contained"
                            color="primary"
                            component="span"
                            startIcon={<AddIcon />}
                            >
                            New Field
                        </Button>
                    </Tooltip>
                </div>
            </Grid>
            <Grid item xs className={classes.formContainer}>
                {(fields.length == 0) ? (
                    <Typography>
                        When you create a new field you will be able to select the details here.  Click "+ New Field" to get started!
                    </Typography>
                ) : (activeFieldKey === null) ? (
                    <Typography>
                        Click on any field to edit the details, or click "+ New Field" to add a new field.
                    </Typography>
                ) : (
                    <div >
                        <Typography variant='h6' gutterBottom>
                            Edit Field
                        </Typography>
                        <Typography
                            variant='subtitle2'
                            color="textSecondary"
                            gutterBottom
                            className={classes.marginTop}
                            >
                            Select the type of field you would like to add.
                        </Typography>
                        <TextField
                            id="select-field-type"
                            select
                            value={fields[activeFieldKey].options.type}
                            onChange={event => handleFields('type', event)}
                            variant="outlined"
                            fullWidth
                            error={isError('type')}
                            helperText={fieldSelectHelperText}
                            >
                            {fieldTypes.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                            <MenuItem key="paymentform" value="paymentform" disabled={paymentField}>
                                Payment Form (Only 1 allowed per form)
                            </MenuItem>
                        </TextField>
                        {(fields[activeFieldKey].options.type === 'image') &&
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => openSelectFileDialog("image")}
                                fullWidth
                                className={classes.marginTop}
                                >
                                Select Image
                            </Button>
                            <div className={classes.buttonGroupBorder}>
                                <RichImageSlider
                                    valueLabelDisplay="auto"
                                    value={fields[activeFieldKey].options.image_width}
                                    min={5}
                                    step={5}
                                    max={100}
                                    marks
                                    aria-label="Image Width"
                                    onChange={(e, value) => handleImageWidth(value)}
                                />
                            </div>
                        </>
                            
                        }
                        {(fields[activeFieldKey].options.type !== 'divider') &&
                            <>
                                <Typography
                                    variant='subtitle2'
                                    color="textSecondary"
                                    gutterBottom
                                    className={classes.marginTop}
                                    >
                                    {noLabel.includes(fields[activeFieldKey].options.type) ? 'Enter your text here.' : 'Give the field a label.'}
                                </Typography>
                                <TextField
                                    id="field-label"
                                    value={fields[activeFieldKey].options.label}
                                    onChange={event => handleFields('label', event)}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={isError('label')}
                                />
                            </>
                        }
                        {(fields[activeFieldKey].options.type === 'options') &&
                            <div className={classes.options}>
                                <Typography
                                    variant='subtitle2'
                                    color="textSecondary"
                                    gutterBottom
                                    className={classes.marginTop}
                                    >
                                    Edit options here...
                                </Typography>
                                {fields[activeFieldKey].options.options.map((o, i) => (
                                    <FormControl fullWidth className={classes.option} variant="outlined" key={i}>
                                        <OutlinedInput
                                            key={i}
                                            value={o}
                                            placeholder="Write an Option"
                                            onChange={event => handleOption(event, i)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="remove item"
                                                        onClick={() => removeOption(i)}
                                                        >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>

                                ))}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={addOption}
                                    fullWidth
                                    >
                                    Add Option
                                </Button>
                            </div>
                        }
                        {(! notTextFields.includes(fields[activeFieldKey].options.type) || fields[activeFieldKey].options.type === 'checkbox') &&
                            <>
                                <Typography
                                    variant='subtitle2'
                                    color="textSecondary"
                                    gutterBottom
                                    className={classes.marginTop}
                                    >
                                    Add some helper text.
                                </Typography>
                                <TextField
                                    id="field-helper"
                                    value={fields[activeFieldKey].options.helper_text}
                                    onChange={event => handleFields('helper_text', event)}
                                    variant="outlined"
                                    fullWidth
                                    error={isError('helper_text')}
                                    helperText={(fields[activeFieldKey].options.type === 'upload') ? "Helper text will be visible when you hover the cursor over the button" : "Helper text will appear beneath the field."}
                                />
                            </>
                        }
                        {strictSelectors.includes(fields[activeFieldKey].options.type) &&
                            <>
                                <Typography variant='subtitle2' color="textSecondary" gutterBottom className={classes.marginTop}>
                                    How many options may be selected?
                                </Typography>
                                <TextField
                                    id="input-length"
                                    value={fields[activeFieldKey].options.length}
                                    onChange={event => handleFields('length', event)}
                                    fullWidth
                                    type="number"
                                    error={isError('length')}
                                    variant="outlined"
                                />
                            </>
                        }
                        {(fields[activeFieldKey].options.type === 'emailbutton') &&
                            <>
                                <TextField
                                    id="select-email-field"
                                    select
                                    label="Field to use for Email Address"
                                    value={fields[activeFieldKey].options.email_field}
                                    onChange={event => handleFields('email_field', event)}
                                    variant="outlined"
                                    fullWidth
                                    helperText="Select a field to use for the email address"
                                    required
                                    margin="normal"
                                    >
                                    <MenuItem value={''}>
                                        No Field Selected
                                    </MenuItem>
                                    {emailFields.map((option, i) => (
                                        <MenuItem value={option} key={i}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    id="select-email-template"
                                    select
                                    label="Select the Email Template to Send"
                                    value={fields[activeFieldKey].options.email_template_id}
                                    onChange={event => handleFields('email_template_id', event)}
                                    variant="outlined"
                                    fullWidth
                                    helperText="Select a field to use for the email address"
                                    required
                                    margin="normal"
                                    >
                                    <MenuItem value={0}>
                                        No Template Selected
                                    </MenuItem>
                                    {templates.map(t => (
                                        <MenuItem value={t.id} key={t.id}>
                                            {t.template_title}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </>
                        }
                        {(fields[activeFieldKey].options.type === 'paymentform') &&
                            <>
                                <TextField
                                    id="select-payment-amount-field"
                                    select
                                    label="Field to use for Payment Amount"
                                    value={fields[activeFieldKey].options.payment_amount_field}
                                    onChange={event => handleFields('payment_amount_field', event)}
                                    variant="outlined"
                                    fullWidth
                                    helperText="Select a field to use for the Payment Amount"
                                    required
                                    margin="normal"
                                    className={classes.marginTop}
                                    >
                                    <MenuItem value={''}>
                                        No Field Selected
                                    </MenuItem>
                                    {currencyFields.map((option, i) => (
                                        <MenuItem value={option} key={i}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    id="select-email-field"
                                    select
                                    label="Field to use for Email Address"
                                    value={fields[activeFieldKey].options.email_field}
                                    onChange={event => handleFields('email_field', event)}
                                    variant="outlined"
                                    fullWidth
                                    helperText="Select a field to use for the email address"
                                    required
                                    margin="normal"
                                    >
                                    <MenuItem value={''}>
                                        No Field Selected
                                    </MenuItem>
                                    {emailFields.map((option, i) => (
                                        <MenuItem value={option} key={i}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Typography variant='subtitle2' color="textSecondary" gutterBottom className={classes.marginTop}>
                                    Input the ID of the user to attach anonymous donations to:
                                </Typography>
                                <TextField
                                    id="input-payment-user"
                                    value={fields[activeFieldKey].options.payment_user}
                                    onChange={event => handleFields('payment_user', event)}
                                    fullWidth
                                    type="number"
                                    variant="outlined"
                                    required
                                />
                            </>
                        }
                        <Grid
                            container
                            spacing={3}
                            direction="column"
                            className={classes.marginTop}
                            >
                            {! notTextFields.includes(fields[activeFieldKey].options.type) &&
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={fields[activeFieldKey].options.required}
                                            onChange={event => handleFields('required', event)}
                                            color="primary"
                                            value="required"
                                        />
                                    }
                                    label="Make this a required field?"
                                    labelPlacement="end"
                                    margin="normal"
                                />
                            </Grid>
                            }
                            {strictSelectors.includes(fields[activeFieldKey].options.type) &&
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fields[activeFieldKey].options.strict_select}
                                                onChange={event => handleFields('strict_select', event)}
                                                color="primary"
                                                value="strict_select"
                                            />
                                        }
                                        label={`Require users to make ${fields[activeFieldKey].options.length} selection(s)?`}
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                            }
                            {forTitle.includes(fields[activeFieldKey].options.type) && (type !== "club") &&
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fields[activeFieldKey].options.title}
                                                onChange={event => handleFields('title', event)}
                                                color="primary"
                                                value="title"
                                            />
                                        }
                                        label="Would you like this field to be part of the entry title?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                            }
                            {forSubtitle.includes(fields[activeFieldKey].options.type) &&
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fields[activeFieldKey].options.subtitle}
                                                onChange={e => handleFields('subtitle', e)}
                                                color="primary"
                                                value="subtitle"
                                            />
                                        }
                                        label="Would you like this field to be part of the entry subtitle?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                            }
                            {(allOptions.length > 0) &&
                                <Grid item>
                                    <TextField
                                        id="select-conditional"
                                        select
                                        label="Conditional Visibility"
                                        value={fields[activeFieldKey].options.conditional}
                                        onChange={event => handleFields('conditional', event)}
                                        variant="outlined"
                                        fullWidth
                                        helperText="Select an option from an options field that will make this field visible."
                                        >
                                        <MenuItem value={''}>
                                            Always Visible
                                        </MenuItem>
                                        {allOptions.map((option, i) => (
                                            <MenuItem value={option} key={i}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            }
                            {forSubtitle.includes(fields[activeFieldKey].options.type) && (type !== "custom") &&
                                <>
                                    <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={fields[activeFieldKey].options.table}
                                                    onChange={e => handleFields('table', e)}
                                                    color="primary"
                                                    value="table"
                                                />
                                            }
                                            label="Would you like this field to show on the entries table by default?"
                                            labelPlacement="end"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={fields[activeFieldKey].options.order_by}
                                                    onChange={e => handleFields('order_by', e)}
                                                    color="primary"
                                                    value="order_by"
                                                    disabled={!fields[activeFieldKey].options.table}
                                                />
                                            }
                                            label="Would you like this field to order entries by default?"
                                            labelPlacement="end"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            id="select-order"
                                            select
                                            label="Direction to order Entries"
                                            value={fields[activeFieldKey].options.order ?? 'asc'}
                                            onChange={event => handleFields('order', event)}
                                            variant="outlined"
                                            fullWidth
                                            error={isError('order')}
                                            disabled={!fields[activeFieldKey].options.order_by}
                                            >
                                            <MenuItem value={'asc'}>
                                                Ascending
                                            </MenuItem>
                                            <MenuItem value={'desc'}>
                                                Descending
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                </>
                            }
                            {(election && election.recommendations_active === true) &&
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fields[activeFieldKey].options.viewable_for_recommendations}
                                                onChange={event => handleFields('viewable_for_recommendations', event)}
                                                color="primary"
                                                value="viewable_for_recommendations"
                                            />
                                        }
                                        label="Would you like this field to be viewable during recommendations?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                            }
                            {(election && election.voting_active === true) &&
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={fields[activeFieldKey].options.viewable_for_voting}
                                                onChange={event => handleFields('viewable_for_voting', event)}
                                                color="primary"
                                                value="viewable_for_voting"
                                            />
                                        }
                                        label="Would you like this field to be viewable during voting?"
                                        labelPlacement="end"
                                        margin="normal"
                                    />
                                </Grid>
                            }
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={fields[activeFieldKey].options.admin_edit}
                                            onChange={e => handleFields('admin_edit', e)}
                                            color="primary"
                                            value="admin_edit"
                                        />
                                    }
                                    label="Would you like this field to only be editable by administrators?"
                                    labelPlacement="end"
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={fields[activeFieldKey].options.admin_view}
                                            onChange={e => handleFields('admin_view', e)}
                                            color="primary"
                                            value="admin_view"
                                        />
                                    }
                                    label="Would you like this field to only be viewable to administrators?"
                                    labelPlacement="end"
                                    margin="normal"
                                    disabled={fields[activeFieldKey].options.admin_edit === false}
                                />
                            </Grid>
                            {! noAlign.includes(fields[activeFieldKey].options.type) &&
                                <Grid item>
                                    <TextField
                                        id="select-align"
                                        select
                                        label="Alignment"
                                        value={fields[activeFieldKey].options.align}
                                        onChange={event => handleFields('align', event)}
                                        variant="outlined"
                                        fullWidth
                                        error={isError('type')}
                                        helperText={fields[activeFieldKey].options.type==='upload' ? "To add text to your image upload button type in the field below." : null}
                                        >
                                        {alignment.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            }
                            {(fields[activeFieldKey]?.options?.type == 'userselect') &&
                                <Grid item>
                                    <TextField
                                        id="group-select"
                                        select
                                        value={fields[activeFieldKey].options.group_id}
                                        onChange={event => handleFields('group_id', event)}
                                        error={isError('group_id')}
                                        variant="outlined"
                                        fullWidth
                                        color="primary"
                                        margin="normal"
                                        label="Select the group (including subs) that the user select can choose from"
                                        >
                                        <MenuItem key={0} value={0}>
                                            All Users
                                        </MenuItem>
                                        {groups.map(group => (
                                            <MenuItem key={group.id} value={group.id}>
                                                {group.title}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            }
                        </Grid>
                        <Grid
                            container
                            spacing={3}
                            justify="flex-end"
                            className={classes.marginTop}
                            >
                            <Tooltip title="Delete">
                                <RedButton
                                    size="small"
                                    onClick={e => handleDelete(activeFieldKey, e)}
                                    >
                                    Delete Field
                                </RedButton>
                            </Tooltip>
                        </Grid>
                    </div>
                )}
            </Grid>
            <SelectFileDialog
                open={selectFileDialogOpen}
                onSelect={closeSelectFileDialogAndAddImage}
                onClose={closeSelectFileDialog}
                type={selectFileType}
            />
        </Grid>
    )
}
